import { toppingsTotalGrosze } from "./cartPricing";
import jsPDF from "jspdf";
import { useVenuesDataStore } from "@/store/useVenuesDataStore";
import i18n from "i18next";

const SERVICE_FEE_RATE = 0.005; // 0.5%
const TAX_RATE = 0.23; // 23% VAT

interface ReceiptDish {
  name: string;
  size?: string;
  toppings: string[];
  price: number; // in grosz
}

function t(key: string): string {
  return i18n.t(key, { ns: "common" }) ?? key;
}

function getTranslation(
  translations: { languageCode: string; name: string }[],
  lang: string,
): string {
  return (
    translations?.find((t) => t.languageCode === lang)?.name ??
    translations?.[0]?.name ??
    ""
  );
}

function collectSelectedDishes(lang: string): ReceiptDish[] {
  const {
    selectedDishIds,
    remainingGuests = [],
    paidGuests = [],
  } = useVenuesDataStore.getState();

  const allGuests = [...(remainingGuests ?? []), ...(paidGuests ?? [])];
  const allDishes = allGuests.flatMap((g) => g.dishes);

  const idSet = new Set(selectedDishIds);
  const matched = allDishes.filter((d) => idSet.has(d.id));

  return matched.map((d) => ({
    name: getTranslation(d.translations, lang),
    size: d.size ? getTranslation(d.size.translations, lang) : undefined,
    toppings: d.toppings.map((tp) => getTranslation(tp.translations, lang)),
    price: (d.size?.price ?? d.price) + toppingsTotalGrosze(d.toppings),
  }));
}

function fmt(grosz: number): string {
  return (grosz / 100).toFixed(2) + " PLN";
}

function fmtZl(zl: number): string {
  return zl.toFixed(2) + " PLN";
}

export function generateReceipt(lang: string = "en") {
  const { splitMethod, selectedAmount, tipAmount, email, tableNumber } =
    useVenuesDataStore.getState();

  // A4 page
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const marginL = 25;
  const marginR = 25;
  let y = 30;

  const lineHeight = 7;

  // ── Helpers ──
  const addLine = (
    left: string,
    right?: string,
    opts?: { bold?: boolean; size?: number },
  ) => {
    const size = opts?.size ?? 10;
    doc.setFontSize(size);
    doc.setFont("helvetica", opts?.bold ? "bold" : "normal");
    doc.text(left, marginL, y);
    if (right) {
      doc.text(right, pageW - marginR, y, { align: "right" });
    }
    y += lineHeight;
  };

  const addSeparator = () => {
    doc.setDrawColor(180);
    doc.setLineWidth(0.3);
    doc.line(marginL, y, pageW - marginR, y);
    y += 5;
  };

  // ── Header ──
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(t("receipt.title"), pageW / 2, y, { align: "center" });
  y += 12;

  addLine(`${t("receipt.date")}:`, new Date().toLocaleDateString(lang));
  if (tableNumber) {
    addLine(`${t("receipt.table")}:`, String(tableNumber));
  }
  if (email) {
    addLine(`${t("receipt.email")}:`, email);
  }
  y += 4;
  addSeparator();

  // ── Items or Amount ──
  const isItemsBased = splitMethod === "items";
  let subtotalGrosze = 0;

  if (isItemsBased) {
    const dishes = collectSelectedDishes(lang);

    addLine(t("receipt.items"), undefined, { bold: true, size: 12 });
    y += 2;

    // Table header
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("#", marginL, y);
    doc.text(lang === "pl" ? "Nazwa" : "Item", marginL + 10, y);
    doc.text(lang === "pl" ? "Cena" : "Price", pageW - marginR, y, {
      align: "right",
    });
    y += lineHeight;
    addSeparator();

    dishes.forEach((dish, idx) => {
      const label = dish.size ? `${dish.name} (${dish.size})` : dish.name;
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(String(idx + 1), marginL, y);
      doc.text(label, marginL + 10, y);
      doc.text(fmt(dish.price), pageW - marginR, y, { align: "right" });
      y += lineHeight;

      dish.toppings.forEach((tp) => {
        doc.setFontSize(8);
        doc.setFont("helvetica", "italic");
        doc.setTextColor(100);
        doc.text(`  + ${tp}`, marginL + 10, y);
        y += lineHeight - 1;
      });
      doc.setTextColor(0);

      subtotalGrosze += dish.price;
    });
  } else {
    addLine(t("receipt.paymentSummary"), undefined, { bold: true, size: 12 });
    y += 2;
    subtotalGrosze = Math.round(selectedAmount * 100);
    addLine(t("receipt.amountPaid"), fmt(subtotalGrosze));
  }

  y += 2;
  addSeparator();

  // ── Subtotal ──
  const subtotalZl = subtotalGrosze / 100;
  addLine(t("receipt.subtotal"), fmtZl(subtotalZl));

  // ── Service fee 0.5% ──
  const serviceFee = subtotalZl * SERVICE_FEE_RATE;
  addLine(t("receipt.serviceFee"), fmtZl(serviceFee));

  // ── Tax ──
  const netAmount = (subtotalZl + serviceFee) / (1 + TAX_RATE);
  const taxAmount = subtotalZl + serviceFee - netAmount;
  addLine(t("receipt.vat"), fmtZl(taxAmount));

  // ── Tip ──
  if (tipAmount > 0) {
    addLine(t("receipt.tip"), fmtZl(tipAmount));
  }

  addSeparator();

  // ── Total ──
  const total = subtotalZl + serviceFee + tipAmount;
  addLine(t("receipt.total"), fmtZl(total), { bold: true, size: 14 });

  y += 10;

  // ── Footer ──
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(t("receipt.thankYou"), pageW / 2, y, { align: "center" });
  y += 6;
  doc.setFontSize(8);
  doc.setTextColor(130);
  doc.text(t("receipt.poweredBy"), pageW / 2, y, { align: "center" });
  doc.setTextColor(0);

  doc.save("receipt.pdf");
}
